import React, { useState, useEffect, useRef, forwardRef } from 'react'
import { Link } from 'react-router-dom'

import useTransitionStyles from './useTransitionStyles';

import Sidebar from 'components/Sidebar';
import Avatar from 'components/Avatar';
import ROUTES from 'utils/routes.js';
import { useUser } from 'utils/userContext';

import PopupTooltip from 'components/PopupTooltip';

const RoadmapLesson = forwardRef(({ shiftUp, title, completed, locked, admin, filled, lessonLink, firstLesson, test = false, skeleton = false, showMaxTooltip = false, maxTooltipText = "", maxLocked = false, currentConceptProgress = 0 }, ref) => {

    const [localFilled, setLocalFilled] = useState(filled ?? -1);

    useEffect(() => {
        setTimeout(() => {
            setLocalFilled(filled ?? -1);
        }, 100);
    }, [filled]);

    // For display purposes: show as locked if it's max-locked, even for admins
    const displayLocked = maxLocked || locked;

    return <div ref={ref} className='select-none flex flex-col items-center justify-center _noDrag h-fit relative z-50' style={{ backgroundColor: test ? "var(--background)" : "transparent" }}>
        <Link
            to={(!locked || admin) ? lessonLink : '#'}
            disabled={locked && !admin}
            className='_noDrag relative group flex flex-col items-center justify-center rounded-3xl'
            style={{ marginTop: shiftUp ? "0" : "125px", cursor: locked && !admin ? "default" : "pointer", transition: "margin-top 0.3s ease-in-out", opacity: (displayLocked && !test) ? 0.4 : 1, padding: test ? "0" : "2rem 4rem" }}
            onMouseDown={(e) => {
                // Prevent middle-click from opening in new tab
                if (e.button === 1) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
            onMouseUp={(e) => {
                // Prevent middle-click from opening in new tab
                if (e.button === 1) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
            onClick={(e) => {
                // Prevent middle-click navigation
                if (e.button === 1) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
            onAuxClick={(e) => {
                // Block all auxiliary button clicks (middle-click, etc)
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            {firstLesson && filled === 0 && !completed && <PopupTooltip
                text="Click here to start your first lesson!"
                open={true}
                storageKey="seenFirstLessonPopup"
                autoMarkSeenAfter={5000}
                top="100%"
                width="200px"
            />}
            {firstLesson && filled > 0 && currentConceptProgress > 0 && currentConceptProgress < 3 && !completed && <PopupTooltip
                text="Great start! Practice this lesson until you've mastered it to move forward."
                open={true}
                storageKey="seenFirstLessonProgressPopup"
                autoMarkSeenAfter={7000}
                top="100%"
                width="240px"
            />}
            {showMaxTooltip && <PopupTooltip
                text={maxTooltipText}
                open={true}
                storageKey={null}
                top={shiftUp ? null : "100%"}
                bottom={shiftUp ? "100%" : null}
                width="260px"
            />}
            {test ?
                <div className='relative z-10 overflow-hidden w-24 h-24 rounded-full transition-transform group-hover:-translate-y-1  group-active:translate-y-1 flex items-center justify-center'>
                    <LockIcon className='w-20 h-20' color={completed ? "var(--accent)" : displayLocked ? "var(--primary-dark)" : "var(--primary)"} lineWidth={1.5} opened={completed} />
                </div>
                :
                <div className='relative z-10 overflow-hidden w-24 h-24 rounded-full transition-transform group-hover:-translate-y-1  group-active:translate-y-1 flex items-center justify-center' style={{ backgroundColor: skeleton ? "var(--border)" : completed ? "var(--accent)" : displayLocked ? "var(--primary-dark)" : "var(--primary)", boxShadow: skeleton ? "" : completed ? "0 7px 15px -2px var(--accent-dark)" : localFilled > 0 ? `0 ${localFilled > (1 / 2) ? "3" : "5"}px ${localFilled > (1 / 2) ? "8" : "3"}px -1px var(--accent-dark)` : "0 8px 10px -5px var(--primary-dark)" }}>
                    {(!completed && !displayLocked) && <div className='absolute bg-accent w-[500px] -z-10 bottom-0 left-0 _roadmapWave'
                        style={{
                            top: `${(1 - (localFilled + 0.2)) * 100}%`,
                            clipPath: 'path("M0 0 Q18.75 20 37.5 0 Q56.25 20 75 0 Q93.75 20 112.5 0 Q131.25 20 150 0 Q168.75 20 187.5 0 Q200.25 20 218.75 0 V100 H0 Z")'
                        }}></div>}
                    {displayLocked ? <LockIcon className='w-14 h-14' /> : completed ? <DoneIcon className='w-14 h-14' /> : null}
                </div>}
            {!test && (skeleton ? <div className='w-28 h-8 rounded-lg bg-border mt-6'></div> : <h1 className='mt-6 text-2xl text-nowrap font-bold'>{title}</h1>)}
        </Link>
    </div>
});

function Roadmap() {
    const currentLessonRef = useRef(null);
    const didInitialScroll = useRef(false);

    const { user, loading } = useUser();


    const fadeStyles = {
        hidden: {
            opacity: 0,
            transition: "opacity 400ms ease-in-out"
        },
        show: {
            opacity: 1,
            transition: "opacity 400ms ease-in-out"
        }
    };

    const [transitionStyle, setTransition] = useTransitionStyles(fadeStyles, 'hidden');
    // Trigger the fade-in on mount
    useEffect(() => {
        setTransition('show');
    }, []);


    const [lessonData, setLessonData] = useState([]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            // const { data, error } = await supabase.storage.from('units').download('lessons.json');
            // if (error) {
            //     console.error("Error fetching lesson data:", error);
            //     return;
            // }
            // use local file for development
            let data
            try {
                data = await fetch('/lessons.json');
                if (!data.ok) {
                    console.error("Error fetching lesson data:", data.statusText);
                    return;
                }
            }
            catch (error) {
                console.error("Error fetching lesson data:", error);
                return;
            }

            const concepts = JSON.parse(await data.text());
            if (!cancelled && concepts) {
                // setLessonData(concepts);
                setLessonData(concepts);
            }
        })();


        return () => {
            cancelled = true;
        };
    }, [setLessonData]);

    let currentUnit = user?.currentUnit || 1;
    let currentConcept = user?.currentConcept || 1;
    let currentConceptProgress = user?.currentConceptProgress || 1;

    // Get max access limits (null means unlimited)
    const maxUnit = user?.maxUnit || null;
    const maxConcept = user?.maxConcept || null;

    // Track if we've found the first max-locked lesson
    let foundFirstMaxLocked = false;

    const scrollRef = useRef(null);
    const scrollVelocity = useRef(0);
    const ticking = useRef(false);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;


        const onWheel = (e) => {
            const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);
            if (!e.shiftKey && !isHorizontalScroll) {
                e.preventDefault();
                scrollVelocity.current += e.deltaY;

                if (!ticking.current) {
                    ticking.current = true;
                    requestAnimationFrame(smoothScroll);
                }
            }
        };

        const smoothScroll = () => {
            const el = scrollRef.current;
            if (!el) return;

            el.scrollLeft += scrollVelocity.current * 0.05;
            scrollVelocity.current *= 0.9;

            if (Math.abs(scrollVelocity.current) > 0.5) {
                requestAnimationFrame(smoothScroll);
            } else {
                scrollVelocity.current = 0;
                ticking.current = false;
            }
        };



        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    useEffect(() => {
        if (
            !loading && user &&
            !didInitialScroll.current &&
            lessonData.length &&
            currentLessonRef.current
        ) {
            currentLessonRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center' });
            didInitialScroll.current = true;
        }
    }, [lessonData, user, loading]);

    return (
        <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0" >
            <Sidebar></Sidebar>
            <div className='Roadmap flex-grow h-full bg-background text-text text-center grid  overflow-hidden' >
                <div className='flex flex-col w-full overflow-x-hidden'>
                    <div className='flex justify-between px-4 lg:px-6 w-full'>
                        <div>
                            {/* <h1 className='font-bold text-center text-[1.8rem] lg:text-[2rem]'>Lessons</h1> */}
                        </div>
                        <div className="flex justify-end items-center">
                            {user?.isGuest ? (
                                <div className='flex items-center gap-4'>You are in guest mode. <Link to={ROUTES.SIGNUP} className="bg-background-light text-white font-bold px-4 py-2 rounded-2xl shadow-md hover:bg-background transition-all duration-500 text-sm border-2 border-primary">CREATE AN ACCOUNT</Link></div>
                            ) : (
                                <Avatar size={"sm"}></Avatar>
                            )}
                        </div>
                    </div>

                    <div
                        className="Lessons max-h-full w-full max-w-full grow overflow-x-auto flex justify-start items-center px-2 "
                        ref={scrollRef}
                        style={transitionStyle}>
                        {lessonData?.length ? lessonData.map((unit, unitIndex) => {
                            if (loading || (unit["unit"] === "DEVELOPMENT" && !user?.is_admin)) return null;

                            // Check if this unit is beyond max limits
                            const isUnitBeyondMax = maxUnit !== null && unitIndex + 1 > maxUnit;

                            return <div key={unit["unit"]} className='relative px-8 b w-fit border-x-2 border-dashed border-border py-5 mb-2 tall:mb-20' style={{ borderRightColor: !unit.test || unitIndex === lessonData.length - 1 ? "var(--border)" : (isUnitBeyondMax ? "var(--primary-dark)" : currentUnit > unitIndex + 1 ? "var(--accent)" : "var(--primary-dark)"), borderLeftColor: (!lessonData[unitIndex - 1]?.test || unitIndex === 0) ? "var(--border)" : (isUnitBeyondMax ? "var(--primary-dark)" : currentUnit > unitIndex ? "var(--accent)" : "var(--primary-dark)") }}>
                                <div className='mb-4 sticky left-8 w-fit bg-background-light z-10 p-4 min-w-64 rounded-xl flex flex-col justify-start'>
                                    <h1 className='text-base font-bold text-text-light w-fit h-fit text-left leading-[0.75] mt-[5px]'>Unit {unitIndex + 1}</h1>
                                    <h1 className='text-xl font-bold w-fit h-fit text-left'>{unit["unit"]}</h1>
                                </div>
                                <div className='flex flex-row w-full'>
                                    {unit.concepts?.map((concept, conceptIndex) => {
                                        const isGuestFirstLesson = user?.isGuest && unitIndex === 0 && conceptIndex === 0;
                                        const isComplete = unitIndex + 1 < currentUnit || (unitIndex + 1 === currentUnit && conceptIndex + 1 < currentConcept);
                                        const isCurrent = unitIndex + 1 === currentUnit && conceptIndex + 1 === currentConcept;
                                        const isLocked = unitIndex + 1 > currentUnit || (unitIndex + 1 === currentUnit && conceptIndex + 1 > currentConcept);

                                        // Check if lesson is beyond max access limits
                                        const isBeyondMaxUnit = maxUnit !== null && unitIndex + 1 > maxUnit;
                                        const isBeyondMaxConcept = maxUnit !== null && maxConcept !== null &&
                                            unitIndex + 1 === maxUnit && conceptIndex + 1 > maxConcept;
                                        const isAccessLocked = user?.isGuest ? !isGuestFirstLesson : (isBeyondMaxUnit || isBeyondMaxConcept);

                                        // Show tooltip on the current lesson if it's max-locked, OR on the next lesson if it's max-locked
                                        let isFirstMaxLocked = false;
                                        let maxTooltipMessage = "";
                                        if (!foundFirstMaxLocked) {
                                            // Check if this is the current lesson and it's max-locked
                                            const isCurrentAndMaxLocked = isCurrent && isAccessLocked;

                                            // OR check if this is the direct next lesson after current progress AND max-locked
                                            const isDirectNextLesson =
                                                // Next concept in current unit
                                                (unitIndex + 1 === currentUnit && conceptIndex + 1 === currentConcept + 1) ||
                                                // First concept in next unit (if current concept is at the end of previous unit)
                                                (unitIndex + 1 === currentUnit + 1 && conceptIndex === 0 &&
                                                    lessonData[currentUnit - 1]?.concepts?.length === currentConcept);

                                            // Show tooltip if: current lesson is max-locked OR next lesson is max-locked
                                            if (isCurrentAndMaxLocked) {
                                                isFirstMaxLocked = true;
                                                foundFirstMaxLocked = true;
                                                maxTooltipMessage = "Your instructor has limited access past this point. Keep up the great work!";
                                            } else if (isDirectNextLesson && isAccessLocked) {
                                                isFirstMaxLocked = true;
                                                foundFirstMaxLocked = true;
                                                maxTooltipMessage = "Lessons beyond this point are locked by your instructor. Nice progress so far!";
                                            }
                                        }

                                        return (
                                            <RoadmapLesson
                                                key={concept["concept"]}
                                                ref={isCurrent ? currentLessonRef : undefined}
                                                filled={isCurrent ? (currentConceptProgress - 1) / 3 : 0}
                                                completed={isComplete}
                                                shiftUp={conceptIndex % 2}
                                                admin={user?.is_admin}
                                                title={concept["concept"]}
                                                locked={isLocked || isAccessLocked}
                                                maxLocked={isAccessLocked}
                                                lessonLink={
                                                    ROUTES.LESSON.replace(":unitNum", unitIndex + 1).replace(':conceptNum', conceptIndex + 1)
                                                }
                                                firstLesson={unitIndex === 0 && conceptIndex === 0}
                                                showMaxTooltip={isFirstMaxLocked}
                                                maxTooltipText={maxTooltipMessage}
                                                currentConceptProgress={isCurrent ? currentConceptProgress : 0}
                                            />
                                        );
                                    })}
                                </div>
                                <div className='absolute bottom-[50%] -right-[3px] translate-x-1/2 translate-y-1/2 z-10'>

                                    {unit?.test && (() => {
                                        const isComplete = currentUnit > unitIndex + 1;
                                        const isCurrent = currentUnit === unitIndex + 1 && (currentConcept === "test" || currentConcept === 999);
                                        const isLocked = currentUnit < unitIndex + 1 || (currentUnit === unitIndex + 1 && !(currentConcept === "test" || currentConcept === 999));

                                        // Check if test is beyond max access limits
                                        const isBeyondMaxUnit = maxUnit !== null && unitIndex + 1 > maxUnit;
                                        const isAccessLocked = user?.isGuest ? true : isBeyondMaxUnit;

                                        // Show tooltip if this test is current and max-locked, OR if it's the direct next lesson and max-locked
                                        let showTestMaxTooltip = false;
                                        let testTooltipMessage = "";
                                        if (!foundFirstMaxLocked && isAccessLocked) {
                                            // Check if this is the current test and it's max-locked
                                            const isCurrentTestAndMaxLocked = isCurrent;

                                            // OR check if test is the direct next lesson
                                            const isDirectNextTest =
                                                (unitIndex + 1 === currentUnit && (currentConcept === "test" || currentConcept === 999));

                                            if (isCurrentTestAndMaxLocked) {
                                                showTestMaxTooltip = true;
                                                foundFirstMaxLocked = true;
                                                testTooltipMessage = "Your instructor has limited access past this point. Keep up the great work!";
                                            } else if (isDirectNextTest) {
                                                showTestMaxTooltip = true;
                                                foundFirstMaxLocked = true;
                                                testTooltipMessage = "Units beyond this point are locked by your instructor. Nice progress so far!";
                                            }
                                        }


                                        return (<><PopupTooltip
                                            text="Test your knowledge with a unit test!"
                                            open={!!unit.test && (currentUnit === 1 && (currentConcept === "test" || currentConcept === 999))} // Show if it's the first unit and the current concept is the test
                                            storageKey={`seenUnitTestPopup`}
                                            autoMarkSeenAfter={5000}
                                            top="112%"
                                            width="220px"
                                        /><RoadmapLesson
                                                key={`test-${unitIndex}`}
                                                ref={isCurrent ? currentLessonRef : undefined}
                                                completed={isComplete}
                                                admin={user?.is_admin}
                                                title={""}
                                                locked={isLocked || isAccessLocked}
                                                maxLocked={isAccessLocked}
                                                shiftUp={true}
                                                lessonLink={
                                                    ROUTES.LESSON.replace(":unitNum", unitIndex + 1).replace(':conceptNum', "test")
                                                }
                                                test={true}
                                                showMaxTooltip={showTestMaxTooltip}
                                                maxTooltipText={testTooltipMessage}
                                            /></>)
                                    })()}
                                </div>
                            </div>
                        }) : (new Array(2).fill(null).map((_, i) => (
                            <div key={i} className='relative px-8 b w-fit border-x-2 border-dashed border-border py-5 mb-2 tall:mb-20' >
                                <div className='mb-4 sticky left-8 w-fit bg-background-light z-10 p-4 min-w-64 rounded-xl flex flex-col justify-start'>
                                    <h1 className='text-base font-bold text-text-light w-fit h-fit text-left leading-[0.75] mt-[5px]'>Unit {i + 1}</h1>
                                    <div className='w-16 h-6 rounded-lg bg-border mt-2'></div>
                                </div>
                                <div className='flex flex-row w-full'>
                                    {new Array(4).fill(null).map((_, conceptIndex) => (
                                        <RoadmapLesson
                                            key={`loading-concept-${i}-${conceptIndex}`}
                                            shiftUp={conceptIndex % 2}
                                            title={""}
                                            skeleton={true}
                                        />
                                    ))}
                                </div>
                            </div>
                        )))}
                        <div className='relative px-8 b w-fit border-x-2 border-dashed border-border py-5 mb-2 tall:mb-20 border-r-0 pr-0' >
                            <div className='mb-4 sticky left-8 w-fit bg-background-light z-10 p-4 min-w-64 rounded-xl flex flex-col justify-start'>
                                <h1 className='text-base font-bold text-text-light w-fit h-fit text-left leading-[0.75] mt-[5px]'>New Units</h1>
                                <h1 className='text-xl font-bold w-fit h-fit text-left'>Coming soon</h1>
                            </div>
                            <div className='flex flex-row w-full'>
                                {new Array(3).fill(null).map((_, conceptIndex) => (
                                    <RoadmapLesson
                                        key={`loading-comingsoon-${conceptIndex}`}
                                        shiftUp={conceptIndex % 2}
                                        title={"aa"}
                                        skeleton={true}

                                    />
                                ))}
                            </div>
                            <div className='absolute inset-0 z-20' style={{ background: "linear-gradient(to right, transparent -10%, var(--background) 110%)" }}></div>
                        </div>
                    </div>
                </div>

            </div>
        </div >
    )
}

function LockIcon({ className, color = "var(--text)", lineWidth = 0, opened = false }) {
    return opened ? <svg className={className} viewBox="272.15 110.026 42.666 65.5" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M 306.816 131.693 L 307.483 131.693 L 308.15 131.693 L 308.816 131.693 L 309.483 131.693 C 309.85 131.693 310.203 131.726 310.544 131.791 C 310.884 131.857 311.211 131.955 311.525 132.085 C 311.839 132.216 312.14 132.379 312.428 132.575 C 312.715 132.771 312.99 132.999 313.251 133.261 C 313.512 133.523 313.741 133.797 313.937 134.085 C 314.132 134.372 314.295 134.672 314.426 134.986 C 314.556 135.3 314.654 135.627 314.719 135.967 C 314.784 136.307 314.816 136.66 314.816 137.026 L 314.816 143.693 L 314.816 150.36 L 314.816 157.027 L 314.816 163.693 C 314.816 164.06 314.783 164.413 314.718 164.753 C 314.653 165.094 314.556 165.421 314.425 165.735 C 314.295 166.049 314.132 166.35 313.936 166.637 C 313.74 166.925 313.512 167.2 313.251 167.461 C 312.99 167.723 312.716 167.951 312.429 168.147 C 312.142 168.343 311.841 168.506 311.527 168.636 C 311.213 168.767 310.886 168.864 310.545 168.929 C 310.204 168.994 309.85 169.027 309.483 169.026 L 301.483 169.026 L 293.483 169.026 L 285.483 169.026 L 277.483 169.026 C 277.116 169.026 276.763 168.994 276.423 168.928 C 276.082 168.863 275.755 168.766 275.442 168.635 C 275.128 168.504 274.827 168.342 274.54 168.146 C 274.253 167.95 273.979 167.722 273.718 167.461 C 273.457 167.2 273.229 166.926 273.033 166.638 C 272.837 166.351 272.674 166.05 272.543 165.736 C 272.412 165.422 272.314 165.095 272.249 164.754 C 272.183 164.414 272.15 164.06 272.15 163.693 L 272.15 157.026 L 272.15 150.36 L 272.15 143.693 L 272.15 137.026 C 272.15 136.659 272.183 136.306 272.248 135.966 C 272.313 135.625 272.411 135.298 272.542 134.985 C 272.672 134.671 272.836 134.37 273.032 134.083 C 273.228 133.796 273.456 133.522 273.718 133.261 C 273.979 133 274.254 132.772 274.541 132.576 C 274.829 132.38 275.129 132.217 275.443 132.086 C 275.757 131.955 276.084 131.857 276.424 131.792 C 276.764 131.726 277.117 131.693 277.483 131.693 L 278.15 131.693 L 278.817 131.693 L 279.483 131.693 L 280.15 131.693 L 280.15 130.359 L 280.15 129.026 L 280.15 127.692 L 280.15 126.359 C 280.15 125.437 280.231 124.549 280.394 123.695 C 280.556 122.841 280.8 122.021 281.125 121.235 C 281.45 120.449 281.856 119.697 282.344 118.979 C 282.832 118.261 283.401 117.577 284.051 116.927 C 284.701 116.277 285.386 115.709 286.104 115.222 C 286.822 114.734 287.574 114.328 288.36 114.003 C 289.146 113.677 289.966 113.433 290.82 113.271 C 291.674 113.108 292.561 113.026 293.483 113.026 C 294.405 113.025 295.308 113.128 296.181 113.317 C 297.054 113.506 297.897 113.782 298.699 114.129 C 299.501 114.475 300.261 114.893 300.968 115.364 C 301.675 115.836 302.329 116.362 302.918 116.927 C 303.568 117.578 304.138 118.262 304.625 118.981 C 305.113 119.699 305.519 120.451 305.844 121.237 C 306.169 122.023 306.337 122.421 306.574 123.697 C 306.811 124.973 301.853 126.441 301.502 125.173 C 301.151 123.905 301.094 123.748 300.9 123.276 C 300.705 122.804 300.462 122.353 300.17 121.922 C 299.879 121.492 299.538 121.082 299.15 120.693 C 298.761 120.304 298.351 119.964 297.921 119.672 C 297.49 119.38 297.038 119.137 296.566 118.943 C 296.094 118.748 295.601 118.602 295.087 118.505 C 294.573 118.408 294.038 118.359 293.483 118.359 C 292.927 118.359 292.393 118.408 291.879 118.505 C 291.365 118.602 290.872 118.748 290.4 118.943 C 289.927 119.137 289.476 119.38 289.045 119.672 C 288.615 119.964 288.205 120.304 287.816 120.693 C 287.427 121.082 287.087 121.492 286.795 121.922 C 286.503 122.353 286.26 122.804 286.066 123.276 C 285.872 123.748 285.726 124.241 285.629 124.755 C 285.532 125.269 285.483 125.803 285.483 126.359 L 285.483 127.692 L 285.483 129.026 L 285.483 130.359 L 285.483 131.693 L 289.483 131.693 L 293.483 131.693 L 297.483 131.693 L 301.483 131.693 L 304.149 131.693 L 306.816 131.693 Z M 277.483 163.693 L 285.483 163.693 L 293.483 163.693 L 301.483 163.693 L 309.483 163.693 L 309.483 157.027 L 309.483 150.36 L 309.483 143.693 L 309.483 137.026 L 301.483 137.026 L 293.483 137.026 L 285.483 137.026 L 277.483 137.026 L 277.483 143.693 L 277.483 150.36 L 277.483 157.027 L 277.483 163.693 Z M 293.483 155.693 C 293.85 155.693 294.204 155.661 294.544 155.595 C 294.884 155.53 295.211 155.433 295.525 155.302 C 295.839 155.171 296.14 155.008 296.428 154.812 C 296.716 154.616 296.99 154.388 297.251 154.127 C 297.513 153.866 297.741 153.592 297.937 153.305 C 298.132 153.018 298.295 152.717 298.426 152.403 C 298.557 152.089 298.654 151.762 298.719 151.421 C 298.784 151.081 298.816 150.727 298.816 150.359 C 298.816 149.992 298.783 149.638 298.718 149.298 C 298.653 148.958 298.554 148.631 298.424 148.317 C 298.293 148.003 298.13 147.703 297.935 147.415 C 297.739 147.128 297.511 146.855 297.251 146.594 C 296.99 146.334 296.717 146.106 296.43 145.91 C 296.142 145.715 295.842 145.552 295.528 145.421 C 295.214 145.29 294.887 145.192 294.546 145.126 C 294.205 145.061 293.851 145.027 293.483 145.026 C 293.116 145.026 292.762 145.058 292.421 145.122 C 292.08 145.187 291.753 145.285 291.439 145.416 C 291.126 145.547 290.825 145.71 290.538 145.906 C 290.252 146.103 289.978 146.332 289.718 146.594 C 289.458 146.857 289.23 147.132 289.035 147.42 C 288.84 147.708 288.677 148.009 288.546 148.322 C 288.416 148.636 288.317 148.963 288.251 149.302 C 288.185 149.642 288.152 149.994 288.15 150.359 C 288.148 150.725 288.18 151.077 288.245 151.417 C 288.309 151.757 288.407 152.084 288.538 152.398 C 288.668 152.712 288.832 153.013 289.029 153.301 C 289.226 153.589 289.455 153.865 289.718 154.127 C 289.98 154.39 290.256 154.62 290.544 154.816 C 290.833 155.013 291.133 155.176 291.447 155.307 C 291.761 155.437 292.088 155.535 292.427 155.599 C 292.767 155.664 293.119 155.695 293.483 155.693 Z" fill={color} stroke={color} strokeWidth={lineWidth} />
    </svg> : <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 58.6667C14.5333 58.6667 13.2782 58.1449 12.2346 57.1014C11.1911 56.0578 10.6684 54.8018 10.6666 53.3334V26.6667C10.6666 25.2 11.1893 23.9449 12.2346 22.9014C13.28 21.8578 14.5351 21.3351 16 21.3334H18.6666V16C18.6666 12.3111 19.9671 9.16713 22.568 6.56802C25.1688 3.96891 28.3128 2.66847 32 2.66669C35.6871 2.66491 38.832 3.96536 41.4346 6.56802C44.0373 9.17069 45.3368 12.3147 45.3333 16V21.3334H48C49.4666 21.3334 50.7226 21.856 51.768 22.9014C52.8133 23.9467 53.3351 25.2018 53.3333 26.6667V53.3334C53.3333 54.8 52.8115 56.056 51.768 57.1014C50.7244 58.1467 49.4684 58.6685 48 58.6667H16ZM16 53.3334H48V26.6667H16V53.3334ZM32 45.3334C33.4666 45.3334 34.7226 44.8116 35.768 43.768C36.8133 42.7245 37.3351 41.4685 37.3333 40C37.3315 38.5316 36.8097 37.2765 35.768 36.2347C34.7262 35.1929 33.4702 34.6702 32 34.6667C30.5297 34.6631 29.2746 35.1858 28.2346 36.2347C27.1946 37.2836 26.672 38.5387 26.6666 40C26.6613 41.4614 27.184 42.7174 28.2346 43.768C29.2853 44.8187 30.5404 45.3405 32 45.3334ZM24 21.3334H40V16C40 13.7778 39.2222 11.8889 37.6666 10.3334C36.1111 8.7778 34.2222 8.00002 32 8.00002C29.7777 8.00002 27.8888 8.7778 26.3333 10.3334C24.7777 11.8889 24 13.7778 24 16V21.3334Z" fill={color} stroke={color} strokeWidth={lineWidth} />
    </svg>
}

function DoneIcon({ className }) {
    return <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M52.0799 17.0933C52.8506 17.8219 53.3004 18.8267 53.3304 19.8868C53.3604 20.947 52.9682 21.9756 52.2399 22.7466L29.5732 46.7466C29.2059 47.1349 28.7644 47.4456 28.275 47.6604C27.7855 47.8752 27.2579 47.9897 26.7235 47.9971C26.189 48.0046 25.6585 47.9049 25.1632 47.7038C24.6679 47.5028 24.218 47.2045 23.8399 46.8266L11.8399 34.8266C11.1334 34.0684 10.7487 33.0655 10.767 32.0292C10.7853 30.9929 11.2051 30.0042 11.9379 29.2713C12.6708 28.5384 13.6595 28.1187 14.6958 28.1004C15.7321 28.0821 16.735 28.4667 17.4932 29.1733L26.5866 38.2613L46.4266 17.2533C47.1552 16.4826 48.16 16.0328 49.2201 16.0028C50.2803 15.9728 51.3089 16.3651 52.0799 17.0933Z" fill="var(--text)" />
    </svg>
}

export default Roadmap
